/**
 * InvestorMetricModal — detalle fila-a-fila detrás de una KPI card del
 * dashboard del inversor (InvestorOverview).
 *
 * NO usa RPCs nuevas: reutiliza las 3 RPC de inversionista que ya están en
 * producción, ya tienen el chequeo anti-IDOR (SEC-014/015,
 * fix_investor_rpc_idor_emergency_2026_07_27.sql) y ya se consumen desde las
 * vistas InvestorPatients / InvestorConsultas / InvestorRevenue:
 *   - get_investor_patients(p_inversionista_id)
 *   - get_investor_calendar(p_inversionista_id, p_desde, p_hasta)
 *   - get_distribucion_inversionista(p_inversionista_id, p_desde, p_hasta)
 *
 * El modal muestra un SUBCONJUNTO de lo que esas vistas completas ya exponen al
 * mismo inversor autenticado — cero superficie nueva. En particular NO muestra
 * RUT / email / teléfono / previsión del paciente (eso vive en la vista
 * completa "Mis Pacientes", no en un vistazo rápido).
 *
 * Contexto B — tokens de TherapistPortal.css. ModalShell no se portalea.
 */

import React, { useEffect, useState } from 'react';
import ModalShell from '../ui/ModalShell';
import { SkeletonText } from '../ui/Skeleton';
import { supabase } from '../../lib/supabase/client';
import '../ui/metricDetailModal.css';

export type InvestorMetricKey = 'patients' | 'consultsMonth' | 'revenueMonth' | 'revenueTotal';

interface Props {
  investorId: string;
  metric: InvestorMetricKey;
  onClose: () => void;
}

// ─── Shapes de las RPC (subconjunto de columnas usadas) ───────────────────────

interface PatientRow {
  id: string;
  nombre: string;
  apellido: string;
  total_consultas: number;
  ultima_consulta: string | null;
  nombre_terapeuta: string | null;
}

interface CalendarRow {
  id: string;
  fecha_hora: string;
  estado: string;
  tipo_consulta: string;
  modalidad: string;
  nombre_paciente: string;
  nombre_terapeuta: string;
}

interface DistRow {
  distribucion_id: string;
  fecha_consulta: string;
  nombre_paciente: string;
  numero_sesion: number;
  aplica_split: boolean;
  monto_inversionista: number;
  estado_pago: string;
}

// ─── Formato ──────────────────────────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: '2-digit' });

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

const formatCLP = (n: number) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);

const TIPO_LABEL: Record<string, string> = {
  'Terapia Individual': 'Individual',
  'Terapia de Parejas': 'Parejas',
  'Terapia de Pareja': 'Parejas',
  'Terapia Familiar': 'Familiar',
  individual: 'Individual', pareja: 'Parejas', familiar: 'Familiar',
};
const tipoLabel = (t: string) => TIPO_LABEL[t] ?? t;

const ESTADO_META: Record<string, { label: string; tone: 'blue' | 'green' | 'orange' | 'red' }> = {
  agendada:   { label: 'Agendada',   tone: 'blue' },
  programada: { label: 'Programada', tone: 'blue' },
  confirmada: { label: 'Confirmada', tone: 'blue' },
  en_progreso:{ label: 'En progreso',tone: 'blue' },
  completada: { label: 'Completada', tone: 'green' },
  cancelada:  { label: 'Cancelada',  tone: 'red' },
  no_asistio: { label: 'No asistió', tone: 'orange' },
};
const estadoMeta = (e: string) => ESTADO_META[e] ?? { label: e, tone: 'blue' as const };

const PAGO_META: Record<string, { label: string; tone: 'green' | 'orange' | 'blue' }> = {
  pagado:      { label: 'Pagado',      tone: 'green' },
  pago_completo:{ label: 'Pagado',     tone: 'green' },
  pendiente:   { label: 'Pendiente',   tone: 'orange' },
  parcial:     { label: 'Parcial',     tone: 'blue' },
};
const pagoMeta = (e: string) => PAGO_META[e] ?? { label: e, tone: 'orange' as const };

// ─── Metadatos por métrica ────────────────────────────────────────────────────

const META: Record<InvestorMetricKey, { title: string; accent: string }> = {
  patients:     { title: 'Pacientes en cartera', accent: 'var(--apple-blue)' },
  consultsMonth:{ title: 'Consultas este mes',   accent: 'var(--apple-orange)' },
  revenueMonth: { title: 'Ingresos del mes',     accent: 'var(--apple-green)' },
  revenueTotal: { title: 'Ingresos totales',     accent: 'var(--apple-purple)' },
};

// ─── Componente ───────────────────────────────────────────────────────────────

const InvestorMetricModal: React.FC<Props> = ({ investorId, metric, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [patients, setPatients] = useState<PatientRow[]>([]);
  const [consultas, setConsultas] = useState<CalendarRow[]>([]);
  const [dist, setDist] = useState<DistRow[]>([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);

    const run = async () => {
      if (metric === 'patients') {
        // Proyección explícita: la RPC devuelve rut/email/telefono/prevision que
        // el modal NO muestra — con .select() no viajan por la red ni quedan en
        // React state (defense-in-depth; el vistazo rápido no necesita esa PII).
        const { data, error: e } = await supabase
          .rpc('get_investor_patients', { p_inversionista_id: investorId })
          .select('id, nombre, apellido, total_consultas, ultima_consulta, nombre_terapeuta');
        if (e) throw e;
        if (alive) setPatients((data as PatientRow[]) ?? []);
        return;
      }

      if (metric === 'consultsMonth') {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        const iso = (d: Date) => d.toISOString().slice(0, 10);
        const { data, error: e } = await supabase.rpc('get_investor_calendar', {
          p_inversionista_id: investorId,
          p_desde: iso(first),
          p_hasta: iso(last),
        });
        if (e) throw e;
        // La card cuenta estados NO cancelados/no-show — mismo filtro acá.
        const rows = ((data as CalendarRow[]) ?? [])
          .filter(c => c.estado !== 'cancelada' && c.estado !== 'no_asistio');
        if (alive) setConsultas(rows);
        return;
      }

      // revenueMonth · revenueTotal → get_distribucion_inversionista
      let desde: string | null = null;
      let hasta: string | null = null;
      if (metric === 'revenueMonth') {
        const now = new Date();
        desde = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
        hasta = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
      }
      const { data, error: e } = await supabase.rpc('get_distribucion_inversionista', {
        p_inversionista_id: investorId,
        p_desde: desde,
        p_hasta: hasta,
      });
      if (e) throw e;
      if (alive) setDist((data as DistRow[]) ?? []);
    };

    run()
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [investorId, metric]);

  const meta = META[metric];

  return (
    <ModalShell title={meta.title} onClose={onClose} size="lg" accentColor={meta.accent}>
      {loading && <div className="mdm-loading"><SkeletonText lines={5} /></div>}
      {!loading && error && (
        <p className="mdm-empty">No se pudo cargar el detalle. Cerrá y volvé a intentar.</p>
      )}

      {!loading && !error && metric === 'patients' && (
        patients.length === 0
          ? <p className="mdm-empty">Todavía no tenés pacientes asignados a tu cartera.</p>
          : (
            <>
              <p className="mdm-count">{patients.length} {patients.length === 1 ? 'paciente activo' : 'pacientes activos'}</p>
              <ul className="mdm-list">
                {patients.map(p => (
                  <li key={p.id} className="mdm-item">
                    <div className="mdm-item__top">
                      <span className="mdm-item__name">{p.nombre} {p.apellido}</span>
                      <span className="mdm-badge mdm-badge--blue">{p.total_consultas} {p.total_consultas === 1 ? 'consulta' : 'consultas'}</span>
                    </div>
                    <div className="mdm-item__meta">
                      {p.nombre_terapeuta ? `Terapeuta: ${p.nombre_terapeuta}` : 'Sin terapeuta asignado'}
                      {p.ultima_consulta && <> · última: {formatDate(p.ultima_consulta)}</>}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )
      )}

      {!loading && !error && metric === 'consultsMonth' && (
        consultas.length === 0
          ? <p className="mdm-empty">No hay consultas este mes en tu cartera.</p>
          : (
            <>
              <p className="mdm-count">{consultas.length} {consultas.length === 1 ? 'consulta' : 'consultas'} este mes</p>
              <ul className="mdm-list">
                {consultas.map(c => {
                  const est = estadoMeta(c.estado);
                  return (
                    <li key={c.id} className="mdm-item">
                      <div className="mdm-item__top">
                        <span className="mdm-item__name">{c.nombre_paciente}</span>
                        <span className={`mdm-badge mdm-badge--${est.tone}`}>{est.label}</span>
                      </div>
                      <div className="mdm-item__meta">
                        {formatDate(c.fecha_hora)} · {formatTime(c.fecha_hora)} · {tipoLabel(c.tipo_consulta)}
                        {' · '}{c.nombre_terapeuta}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )
      )}

      {!loading && !error && (metric === 'revenueMonth' || metric === 'revenueTotal') && (
        dist.length === 0
          ? <p className="mdm-empty">Todavía no hay sesiones completadas que generen ingresos.</p>
          : (() => {
              const totalMio = dist.reduce((s, r) => s + r.monto_inversionista, 0);
              const conSplit = dist.filter(r => r.aplica_split).length;
              return (
                <>
                  <div className="mdm-revenue">
                    <div className="mdm-revenue__cell mdm-revenue__cell--total-green">
                      <span className="mdm-revenue__label">
                        Tu parte {metric === 'revenueMonth' ? '· este mes' : '· acumulado'}
                      </span>
                      <span className="mdm-revenue__value mdm-revenue__value--hero">{formatCLP(totalMio)}</span>
                      <span className="mdm-revenue__hint">
                        suma de {dist.length} {dist.length === 1 ? 'sesión' : 'sesiones'} completadas
                        {' · '}{conSplit} con split aplicado
                      </span>
                    </div>
                  </div>
                  <ul className="mdm-list">
                    {dist.map(r => {
                      const pg = pagoMeta(r.estado_pago);
                      return (
                        <li key={r.distribucion_id} className="mdm-item">
                          <div className="mdm-item__top">
                            <span className="mdm-item__name">{r.nombre_paciente}</span>
                            <span className={`mdm-badge mdm-badge--${pg.tone}`}>{pg.label}</span>
                          </div>
                          <div className="mdm-item__meta">
                            {formatDate(r.fecha_consulta)} · sesión N° {r.numero_sesion}
                            {r.aplica_split ? ' · split aplicado' : ' · sin split'}
                          </div>
                          <div className="mdm-item__value">{formatCLP(r.monto_inversionista)}</div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              );
            })()
      )}
    </ModalShell>
  );
};

export default InvestorMetricModal;
